import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import useAdminData from "@/lib/useAdminData";
import { AdminHeader, Card, StatusBadge, Empty, ErrorBanner, Loading } from "@/components/admin/ui";
import { formatINR } from "@/lib/format";

export default function AdminOrders() {
  const [statuses, setStatuses] = useState([]);
  const [filter, setFilter] = useState("");
  // RTO / manifested are CARRIER stages (shipping.fulfillmentStatus), not order
  // statuses — a separate filter keeps ALLOWED_TRANSITIONS untouched.
  const [stage, setStage] = useState("");
  const [shippingStages, setShippingStages] = useState(["MANIFESTED", "RTO"]);
  const navigate = useNavigate();

  // The previously loaded order list is kept if a refresh fails — an error must
  // never be presented as "0 orders".
  const { data, loading, error, stale, reload } = useAdminData(
    () => {
      const params = new URLSearchParams();
      if (filter) params.set("status", filter);
      if (stage) params.set("shippingStage", stage);
      const query = params.toString();
      return api.get(query ? `/admin/orders?${query}` : "/admin/orders").then((r) => {
        if (Array.isArray(r.data.statuses) && r.data.statuses.length) setStatuses(r.data.statuses);
        if (Array.isArray(r.data.shippingStages) && r.data.shippingStages.length) {
          setShippingStages(r.data.shippingStages);
        }
        return r.data.orders;
      });
    }
  );
  const orders = data ?? [];

  useEffect(() => { reload(); }, [reload, filter, stage]);

  return (
    <div>
      <AdminHeader title="Orders" subtitle={`${orders.length} orders`} />
      {error && <ErrorBanner className="mb-4" message={error.message} stale={stale} onRetry={reload} />}
      <div className="mb-4 flex flex-wrap gap-2">
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="order-status-filter"
          className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white outline-none focus:border-amber-400">
          <option value="">All statuses</option>
          {statuses.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
        <select value={stage} onChange={(e) => setStage(e.target.value)} data-testid="order-stage-filter"
          className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white outline-none focus:border-amber-400">
          <option value="">All carrier stages</option>
          {shippingStages.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
      </div>
      {loading && !data && !error ? <Loading text="Loading orders…" /> : (
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr><th className="p-3">Order</th><th className="p-3">Date</th><th className="p-3">Items</th><th className="p-3">Total</th><th className="p-3">Payment</th><th className="p-3">Status</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {orders.map((o) => (
                <tr key={o.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/admin/orders/${o.id}`)} data-testid={`order-${o.orderNumber}`}>
                  <td className="p-3 font-mono font-semibold text-slate-900">{o.orderNumber}</td>
                  <td className="p-3 text-xs text-slate-500">{new Date(o.createdAt).toLocaleDateString("en-IN")}</td>
                  <td className="p-3">{o.items.length}</td>
                  <td className="p-3 font-mono">{formatINR(o.total)}</td>
                  <td className="p-3">
                    <span className="text-xs font-semibold">{o.paymentMethod === "COD" ? "COD" : "Razorpay"}</span>
                    <div><StatusBadge status={o.paymentStatus} /></div>
                    {o.paymentMethod === "COD" && o.paymentStatus !== "PAID" &&
                      <span className="text-[11px] text-amber-700">Due {formatINR(o.amountDue ?? o.codAmountDue ?? o.total)}</span>}
                  </td>
                  <td className="p-3">
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-amber-50 text-amber-700">{o.status.replace(/_/g, " ")}</span>
                    {o.shipping?.fulfillmentStatus === "RTO" && (
                      <div>
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-red-100 text-red-700"
                          data-testid={`rto-badge-${o.orderNumber}`}>
                          RTO · {(o.shipping.rtoPhase || "RTO_INITIATED").replace(/^RTO_/, "").replace(/_/g, " ").toLowerCase()}
                        </span>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Genuine empty state only: the backend really returned no orders. */}
          {!error && !loading && orders.length === 0 && <Empty text="No orders yet" />}
          {error && orders.length === 0 && (
            <p className="text-xs text-slate-500 p-3" data-testid="orders-load-failed-note">
              Orders could not be loaded. This is not an empty list — retry above.
            </p>
          )}
        </div>
      </Card>
      )}
    </div>
  );
}
